import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  Illustration,
  type IllustrationKind,
  InlineMessage,
  InputField,
  MenuInput,
  type MenuInputOption,
  ModalIsland,
  TabContent,
  TableSortable,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAlertBanner } from '../../../../shared/app/contexts/AlertBannerContext';
import { ALERT_BANNER_TYPES, LABEL_COUNT_LIMIT, TOKENS } from '../../constants';
import { useManagePageDispatch } from '../../contexts/ManagePageContext';
import { useGetLabels, useSaveLabels } from '../../hooks/useAgentManage';
import {
  areValidKeys,
  areValidValues,
  getCriticalMessage,
  getIsCritical,
  getIsKeyCritical,
  getKeyCriticalMessage,
  getLabelTabColConfig,
  getLabelTabSortConfig,
  shallowEqual,
  sortEditLabels,
} from '../../utils/utilManagepage';
import { type AgentRow } from '../ManagePageTable';

import { getEditLabelRowData, getLabelRowData } from './SidePanelShared';

const LABELS_SORT_SESSION_KEY = 'agent:labels:sort';

type StoredSort = { field: string; direction: 'asc' | 'desc' } | null;

const getStoredLabelsSort = (): StoredSort => {
  try {
    const stored = sessionStorage.getItem(LABELS_SORT_SESSION_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore corrupted or unavailable session storage
  }
  return null;
};

const createNewRow = () => ({
  key: '',
  value: '',
  options: [] as MenuInputOption[],
  isLoading: false,
  hasNextPage: true,
});

interface LabelDetails extends Pick<AgentRow, 'agentId'> {
  labels: Record<string, string>;
}

interface SidePanelLabelsTabProps {
  labelDetails: LabelDetails;
  activeTab: string;
}

/**
 * Labels tab for the agent side panel (tab value `"2"`).
 *
 * Displays a read-only sortable table of the agent's key-value labels.
 * When no labels exist, shows an empty-state illustration with an "Add Labels" CTA.
 *
 * An "Edit Labels" modal allows:
 * - Editing values of existing labels (keys are read-only after creation).
 * - Deleting existing labels.
 * - Adding new label rows with a searchable {@link MenuInput} key picker
 *   (debounced, paginated via {@link useGetLabels}) and a free-text value input.
 *   If no matching key is found, a "(New Key)" option is offered.
 * - Total labels are capped at {@link LABEL_COUNT_LIMIT}.
 *
 * Validation uses {@link getIsCritical} / {@link getIsKeyCritical} for format, length,
 * and duplicate-key checks. The save button is enabled only when the label set differs
 * from the original and all keys/values pass validation.
 *
 * Saves are performed through {@link useSaveLabels}; success/error feedback is surfaced
 * via the shared alert banner from {@link useManagePage}.
 *
 * @param props.labelDetails - Object containing `agentId` and the current `labels` record.
 * @param props.activeTab - Currently active tab value for {@link TabContent} visibility control.
 *
 * @returns Tab content with the labels table (or empty state) and the edit modal.
 */
export default function SidePanelLabelsTab({
  labelDetails,
  activeTab,
}: SidePanelLabelsTabProps) {
  // Hooks
  const intl = useIntl();

  // States
  const [tableColumns, setTableColumns] = useState(getLabelTabColConfig(intl));
  const [labelSort, setLabelSort] = useState<StoredSort>(getStoredLabelsSort);
  const [originalLabels, setOriginalLabels] = useState<Record<string, string>>(
    labelDetails.labels
  );
  const [currentLabels, setCurrentLabels] = useState<Record<string, string>>(
    {}
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newLabels, setNewLabels] = useState<ReturnType<typeof createNewRow>[]>(
    []
  );
  const [searchText, setSearchText] = useState('');
  const [isSaveBtnClicked, setIsSaveBtnClicked] = useState(false);

  // Refs for debounce
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hooks
  const query = useGetLabels(searchText);
  const dispatch = useManagePageDispatch();
  const { show: showAlertBanner } = useAlertBanner();
  const { mutate: saveLabels, isPending } = useSaveLabels({
    onSuccess: (response) => {
      setIsEditModalOpen(false);
      setNewLabels([]);
      setSearchText('');
      setIsSaveBtnClicked(false);
      setOriginalLabels(response);

      dispatch({
        refreshAgent: { [labelDetails.agentId]: { labels: response } },
      });
      showAlertBanner({
        colorTheme: ALERT_BANNER_TYPES.SUCCESS,
        title: intl.formatMessage({
          id: 'yg2HFT',
          defaultMessage: 'Success',
          description: 'Success',
        }),
        description: intl.formatMessage({
          id: 'u7P+zc',
          defaultMessage: 'Labels have been updated successfully.',
          description: 'Labels have been updated successfully.',
        }),
      });
    },
    onError: (errorMessage) => {
      setSearchText('');
      setIsSaveBtnClicked(false);
      setIsEditModalOpen(false);

      showAlertBanner({
        colorTheme: ALERT_BANNER_TYPES.CRITICAL,
        title: intl.formatMessage({
          id: 'KFYqlI',
          defaultMessage: 'Failed',
          description: 'Failed',
        }),
        description: errorMessage,
      });
    },
  });

  // Constants
  const isNoLabel = useMemo(
    () => Object.keys(originalLabels).length === 0,
    [originalLabels]
  );
  const isNoCurrentLabel = useMemo(
    () => Object.keys(currentLabels).length === 0,
    [currentLabels]
  );
  const isLimit = useMemo(
    () =>
      Object.keys(currentLabels).length + newLabels.length >= LABEL_COUNT_LIMIT,
    [currentLabels, newLabels.length]
  );
  const { isSaveButtonEnabled, isNoStateChange } = useMemo(() => {
    const newCurrentLabels = { ...currentLabels };

    newLabels.forEach((nl) => {
      newCurrentLabels[nl.key] = nl.value;
    });

    const isNoStateChange = shallowEqual(originalLabels, newCurrentLabels);
    const isValidLabel =
      areValidValues(Object.values(newCurrentLabels)) &&
      areValidKeys(Object.keys(newCurrentLabels));

    return {
      isNoStateChange,
      isSaveButtonEnabled: !isNoStateChange && isValidLabel,
    };
  }, [originalLabels, currentLabels, newLabels]);

  // Effects
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setIsEditModalOpen(false);
    setNewLabels([]);
    setSearchText('');
    setIsSaveBtnClicked(false);
    setOriginalLabels(labelDetails.labels);
    setCurrentLabels(labelDetails.labels);
  }, [labelDetails.agentId, labelDetails.labels]);

  // Utils
  const getLabelOptions = useCallback(
    async (
      currentSearch: string,
      currentIndex: number,
      isInitial: boolean = false
    ) => {
      // Loading
      setNewLabels((pl) =>
        pl.map((row, i) =>
          i === currentIndex ? { ...row, isLoading: true } : row
        )
      );

      try {
        const result = isInitial
          ? await query.refetch()
          : await query.fetchNextPage();

        const pages = result.data?.pages ?? [];

        // Guard against empty pages (e.g., when API returns error)
        if (pages.length === 0) {
          setNewLabels((pl) =>
            pl.map((row, i) =>
              currentIndex === i
                ? {
                    ...row,
                    hasNextPage: false,
                    options: [
                      {
                        label: `${currentSearch} (New Key)`,
                        value: currentSearch,
                        isChecked: false,
                      },
                    ],
                  }
                : row
            )
          );

          return;
        }

        const allOptions = pages.flatMap((page) =>
          page.data.map((o) => ({
            label: o.key,
            value: o.key,
            isChecked: false,
          }))
        );
        const lastPage = pages[pages.length - 1];

        setNewLabels((pl) =>
          pl.map((row, i) =>
            currentIndex === i
              ? {
                  ...row,
                  hasNextPage: lastPage.pagination.hasNextPage,
                  options:
                    allOptions.length > 0
                      ? allOptions
                      : [
                          {
                            label: `${currentSearch} (New Key)`,
                            value: currentSearch,
                            isChecked: false,
                          },
                        ],
                }
              : row
          )
        );
      } catch {
        setNewLabels((pl) =>
          pl.map((row, i) =>
            currentIndex === i
              ? {
                  ...row,
                  hasNextPage: false,
                  options: [
                    {
                      label: `${currentSearch} (New Key)`,
                      value: currentSearch,
                      isChecked: false,
                    },
                  ],
                }
              : row
          )
        );
      } finally {
        setNewLabels((pl) =>
          pl.map((row, i) =>
            i === currentIndex ? { ...row, isLoading: false } : row
          )
        );
      }
    },
    [query]
  );

  const debouncedGetLabelOptions = useCallback(
    (
      currentSearch: string,
      currentIndex: number,
      isInitial: boolean = false
    ) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(
        () => getLabelOptions(currentSearch, currentIndex, isInitial),
        300
      );
    },
    [getLabelOptions]
  );

  const handleScroll = useCallback(
    (e: React.ChangeEvent, currentIndex: number, currentSearch: string) => {
      const { isLoading, hasNextPage } = newLabels[currentIndex];
      const { scrollTop, scrollHeight, clientHeight } = e.target;

      if (
        scrollHeight - scrollTop === clientHeight &&
        hasNextPage &&
        !isLoading
      ) {
        debouncedGetLabelOptions(currentSearch, currentIndex);
      }
    },
    [newLabels, debouncedGetLabelOptions]
  );

  const handleEditSort = useCallback(
    (columnKey: string, order: 'asc' | 'desc') => {
      setCurrentLabels((prev) =>
        sortEditLabels(prev, { sortBy: columnKey, order })
      );
    },
    []
  );

  const handleReadOnlySort = useCallback(
    (columnKey: string, order: 'asc' | 'desc') => {
      const newSort = { field: columnKey, direction: order };
      setLabelSort(newSort);
      try {
        sessionStorage.setItem(
          LABELS_SORT_SESSION_KEY,
          JSON.stringify(newSort)
        );
      } catch {
        // Ignore session storage write failures
      }
    },
    []
  );

  const sortedOriginalLabels = useMemo(
    () =>
      sortEditLabels(originalLabels, {
        sortBy: labelSort?.field,
        order: labelSort?.direction,
      }),
    [originalLabels, labelSort]
  );

  const labelSortConfigs = useMemo(
    () =>
      getLabelTabSortConfig(intl).map((config) => ({
        ...config,
        sortOptions: config.sortOptions.map((opt) => ({
          ...opt,
          shouldShowCheckIcon: true,
          isChecked:
            labelSort?.field === config.key &&
            labelSort?.direction === opt.value,
        })),
      })),
    [intl, labelSort]
  );

  // Renders
  const renderBody = useCallback(() => {
    if (isNoLabel) {
      return (
        <div className='SidePanelLabelsTab-noLabelContainer'>
          <Illustration kind={'Empty' as IllustrationKind} />
          <Typography kind='h1' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='FWW1pn'
              defaultMessage='No Labels Added'
              description='No Labels Added'
            />
          </Typography>
          <Typography kind='body1' color={TOKENS.CF_NEU_MED}>
            <FormattedMessage
              id='s2VMWC'
              defaultMessage='Labels are key-value pairs that help you classify and organize this agent. You can use labels to drive approvals, certifications, risk scoring, and reporting.'
              description='no labels message'
            />
          </Typography>
          <Button
            kind='filled'
            size='medium'
            leftIcon={<Icon kind='Add' />}
            onClick={() => setIsEditModalOpen(true)}>
            <FormattedMessage
              id='IC3Mmn'
              defaultMessage='Add Labels'
              description='Add Labels'
            />
          </Button>
        </div>
      );
    }

    return (
      <>
        <div className='SidePanelLabelsTab-labelTitleContainer'>
          <Typography kind='body2' color={TOKENS.CF_NEU_MED}>
            <FormattedMessage
              id='GrK+gp'
              defaultMessage='Labels are key-value pairs that help you classify and organize this agent. You can use labels to drive approvals, certifications, risk scoring, and reporting.'
              description='label description'
            />
          </Typography>
          <Button
            kind='outlined'
            size='medium'
            leftIcon={<Icon kind='Add' />}
            onClick={() => setIsEditModalOpen(true)}>
            <FormattedMessage
              id='HblVtU'
              defaultMessage='Edit Labels'
              description='Edit Labels'
            />
          </Button>
        </div>
        <div className='SidePanelLabelsTab-labelTableContainer'>
          <TableSortable
            data={getLabelRowData(sortedOriginalLabels)}
            columns={getLabelTabColConfig(intl)}
            setColumns={setTableColumns}
            sortConfigs={labelSortConfigs}
            columnConfigs={{ isDndDisabled: true }}
            isVirtualizationEnabled={false}
            onSort={handleReadOnlySort}
            className='SidePanelLabelsTab-table'
          />
        </div>
      </>
    );
  }, [isNoLabel, originalLabels, isEditModalOpen]);

  return (
    <TabContent className='SidePanelLabelsTab' value={activeTab} tabValue='2'>
      <div className='SidePanelLabelsTab-labelWrapper'>{renderBody()}</div>
      {isEditModalOpen && (
        <ModalIsland
          isOpen={isEditModalOpen}
          onClose={() => {
            if (!isPending) {
              setIsEditModalOpen(false);
              setIsSaveBtnClicked(false);
            }
          }}
          title={intl.formatMessage({
            id: 'HblVtU',
            defaultMessage: 'Edit Labels',
            description: 'Edit Labels',
          })}
          subtitle={intl.formatMessage({
            id: 'AaaFPZ',
            defaultMessage:
              'Labels are key-value pairs that help you classify and organize this agent. You can use labels to drive approvals, certifications, risk scoring, and reporting.',
            description:
              'Labels are key-value pairs that help you classify and organize this agent. You can use labels to drive approvals, certifications, risk scoring, and reporting.',
          })}
          className='SidePanelLabelsTab-editModal'
          headerIcon={
            <Icon kind='Edit' size='large' color={TOKENS.CF_NEU_BLD} />
          }
          contentBody={
            <div className='SidePanelLabelsTab-editModalContent'>
              {!isNoCurrentLabel && (
                <TableSortable
                  data={getEditLabelRowData(
                    currentLabels,
                    setCurrentLabels,
                    intl,
                    isSaveBtnClicked
                  )}
                  columns={tableColumns}
                  setColumns={setTableColumns}
                  sortConfigs={getLabelTabSortConfig(intl)}
                  columnConfigs={{ isDndDisabled: true }}
                  isVirtualizationEnabled={false}
                  onSort={handleEditSort}
                  className={cx(
                    'SidePanelLabelsTab-table',
                    'SidePanelLabelsTab-table--isEditMode'
                  )}
                />
              )}
              {newLabels.map((newLabel, idx) => {
                const isKeyCritical = Boolean(
                  isSaveBtnClicked &&
                  getIsKeyCritical(newLabel.key, Object.keys(currentLabels))
                );
                const isValueCritical = Boolean(
                  isSaveBtnClicked && getIsCritical(newLabel.value)
                );

                return (
                  <div
                    className={cx(
                      'SidePanelLabelsTab-table',
                      'SidePanelLabelsTab-table--isEditMode'
                    )}>
                    <tbody>
                      <tr className='SidePanelLabelsTab-tableNewRow'>
                        <MenuInput
                          options={newLabel.options}
                          onChange={(selectedOption) => {
                            setNewLabels((pl) =>
                              pl.map((row, i) =>
                                idx === i
                                  ? {
                                      ...row,
                                      key: selectedOption.value ?? '',
                                      options: selectedOption.value
                                        ? row.options.map((o) => ({
                                            ...o,
                                            label:
                                              o.value === selectedOption.value
                                                ? o.label.replace(
                                                    ' (New Key)',
                                                    ''
                                                  )
                                                : o.label,
                                            isChecked:
                                              o.value === selectedOption.value,
                                          }))
                                        : [],
                                    }
                                  : row
                              )
                            );
                          }}
                          placeholder={intl.formatMessage({
                            id: '28AhZz',
                            defaultMessage: 'Select or enter a key',
                            description: 'Select or enter a key',
                          })}
                          searchTerm={newLabel.key}
                          isLoading={newLabel.isLoading}
                          noDataText={
                            newLabel.key.trim().length === 0
                              ? intl.formatMessage({
                                  id: 'eodjKQ',
                                  defaultMessage:
                                    'Type a character to see results',
                                  description:
                                    'Type a character to see results',
                                })
                              : intl.formatMessage({
                                  id: 't/CQGx',
                                  defaultMessage: 'No data available',
                                  description: 'No data available',
                                })
                          }
                          isCritical={isKeyCritical}
                          CriticalHelperText={
                            <InlineMessage
                              text={getKeyCriticalMessage(
                                newLabel.key,
                                Object.keys(currentLabels),
                                intl
                              )}
                              colorTheme='critical'
                              size='small'
                              leftIcon={<Icon kind='AlertCriticalFilled' />}
                            />
                          }
                          onScroll={(e) => handleScroll(e, idx, newLabel.key)}
                          hasMoreOptions={newLabel.hasNextPage}
                          suffixIcon={<Icon kind='Check' size='medium' />}
                          isSearchDisabled
                          loadingText={intl.formatMessage({
                            id: '8jhZJ2',
                            defaultMessage: 'Loading more results',
                            description: 'Loading more results',
                          })}
                          onSearchTermChange={(value: string | null) => {
                            const v = value?.trim() ?? '';

                            setNewLabels((pl) =>
                              pl.map((row, i) =>
                                idx === i
                                  ? { ...row, key: v, options: [] }
                                  : row
                              )
                            );

                            setSearchText(v);

                            if (v.length > 0) {
                              debouncedGetLabelOptions(v.trim(), idx, true);
                            }
                          }}
                          menuItemHeightConfigs={{ height: '2rem' }}
                        />
                        <div className='SidePanelLabelsTab-tableNewRowValueWrapper'>
                          <InputField
                            name='basic'
                            placeholder={intl.formatMessage({
                              id: 'ES5vsI',
                              defaultMessage: 'Value',
                              description: 'Value',
                            })}
                            value={newLabel.value}
                            setValue={(value: string) => {
                              setNewLabels((pl) =>
                                pl.map((row, i) =>
                                  idx === i ? { ...row, value } : row
                                )
                              );
                            }}
                            isCritical={isValueCritical}
                            CriticalHelperText={
                              <InlineMessage
                                text={getCriticalMessage(newLabel.value, intl)}
                                colorTheme='critical'
                                size='small'
                                leftIcon={<Icon kind='AlertCriticalFilled' />}
                              />
                            }
                          />
                          <ButtonIcon
                            size='large'
                            kind='ghost'
                            icon={<Icon kind='Delete' size='medium' />}
                            ariaLabel={intl.formatMessage({
                              id: 'CQ7Wmr',
                              defaultMessage: 'Delete item',
                              description: 'Delete item',
                            })}
                            onClick={() => {
                              setIsSaveBtnClicked(false);
                              setNewLabels((pl) =>
                                pl.filter((_, i) => idx !== i)
                              );
                            }}
                          />
                        </div>
                      </tr>
                    </tbody>
                  </div>
                );
              })}
              <Tooltip
                isDisabled={!isLimit}
                trigger={
                  <Button
                    kind='outlined'
                    size='medium'
                    isDisabled={isLimit}
                    leftIcon={<Icon kind='Add' />}
                    onClick={() => {
                      setIsSaveBtnClicked(false);
                      setNewLabels((pl) => [...pl, createNewRow()]);
                    }}
                    className='SidePanelLabelsTab-editModalNewLabelBtn'>
                    <FormattedMessage
                      id='93fhL8'
                      defaultMessage='New Label'
                      description='New Label'
                    />
                  </Button>
                }
                text={intl.formatMessage(
                  {
                    id: 'fiRlRW',
                    defaultMessage: 'You can add up to {limit} labels only.',
                    description: 'You can add up to {limit} labels only.',
                  },
                  { limit: LABEL_COUNT_LIMIT }
                )}
                isPortalEnabled
                placement='top'
              />
            </div>
          }
          FooterActionBarComp={
            <FooterActionBar
              size='large'
              className='SidePanelLabelsTab-editModalFooter'>
              <div />
              <div className='SidePanelLabelsTab-editModalFooterWrapper'>
                <Button
                  kind='subtle'
                  size='medium'
                  isDisabled={isPending}
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setNewLabels([]);
                    setSearchText('');
                    setIsSaveBtnClicked(false);
                    setCurrentLabels(originalLabels);
                  }}>
                  <FormattedMessage
                    id='js69XA'
                    defaultMessage='Cancel'
                    description='cancel'
                  />
                </Button>
                <div className='SidePanelLabelsTab-editModalFooterSaveBtn'>
                  {isSaveButtonEnabled && (
                    <InlineMessage
                      text={intl.formatMessage({
                        id: 'oBZWkD',
                        defaultMessage: 'Keys cannot be edited after saving.',
                        description: 'Keys cannot be edited after saving.',
                      })}
                      colorTheme='info'
                      size='medium'
                      leftIcon={<Icon kind='AlertInfoFilled' />}
                    />
                  )}
                  <Tooltip
                    isDisabled={!isNoStateChange}
                    trigger={
                      <Button
                        kind={isSaveButtonEnabled ? 'filled' : 'outlined'}
                        size='medium'
                        isDisabled={isPending || isNoStateChange}
                        isLoading={isPending}
                        loaderText={intl.formatMessage({
                          id: '1yxebr',
                          defaultMessage: 'Saving...',
                          description: 'Saving...',
                        })}
                        onClick={() => {
                          if (!isSaveButtonEnabled) {
                            setIsSaveBtnClicked(true);

                            return;
                          }

                          saveLabels({
                            agentId: labelDetails.agentId,
                            labels: {
                              ...currentLabels,
                              ...Object.fromEntries(
                                newLabels.map((row) => [row.key, row.value])
                              ),
                            },
                          });
                        }}>
                        <FormattedMessage
                          id='kY2alH'
                          defaultMessage='Save'
                          description='save'
                        />
                      </Button>
                    }
                    text={intl.formatMessage({
                      id: 'hWfjDW',
                      defaultMessage: 'There are no changes to save.',
                      description: 'There are no changes to save.',
                    })}
                    isPortalEnabled
                    placement='top'
                  />
                </div>
              </div>
            </FooterActionBar>
          }
          classNames={{
            bodyClassName: 'SidePanelLabelsTab-editModalBody',
          }}
        />
      )}
    </TabContent>
  );
}
